// The upload pipeline of the browser (GED-FE-02, docs/plan/08-api.md §5): Uppy runs the multipart
// upload (parts cut like the server cuts them, concurrent PUTs straight to the files origin, retries
// with backoff), and this controller supplies what Uppy needs from the BatiFlow API (create, part
// URLs, complete with an Idempotency-Key, abort) and then follows the file through the antivirus
// check until it is available or refused. React reads it as an external store (`useUploads`).
import type {
  CompletedUploadDtoOutput,
  UploadPartsDtoOutput,
  UploadSessionDtoOutput,
  UploadStatusDtoOutput,
} from '@batiflow/api-client';
import { ApiError, type ApiFetcher } from '@batiflow/client-core';
import AwsS3 from '@uppy/aws-s3';
import Uppy, { type UppyFile } from '@uppy/core';
import {
  classifyError,
  classifyRejection,
  partSizeFor,
  precheck,
  statusLost,
  type Classified,
  type UploadExtras,
  type UploadRow,
  type UploadTarget,
} from './upload-model';
import { uploadTiming } from './upload-timing';

/** What Uppy keeps with each file: the id of its row (also makes every added file unique). */
interface UploadMeta extends Record<string, unknown> {
  rowId: string;
  relativePath: string;
}
type UploadBody = Record<string, never>;
type UploadFile = UppyFile<UploadMeta, UploadBody>;

/** What is known of one upload that exists on the server. */
interface Session {
  spaceId: string;
  uploadId: string;
  partCount: number;
  /** Pre-signed URLs, by part number. */
  urls: Map<number, string>;
  urlsExpireAtMs: number;
  /** Parts whose URL was handed to Uppy: a second request for one is a retry (fresh URL). */
  signed: Set<number>;
  /** Parts the files origin accepted, with their ETag. */
  sent: Map<number, string>;
  refreshing: Promise<void> | null;
  /** One per upload, reused by every attempt to complete it. */
  idempotencyKey: string;
  completed: CompletedUploadDtoOutput | null;
}

export interface UploadHandlers {
  /** The server has the file (a document or version in `scanning`): lists can show it. */
  onCompleted?: (row: UploadRow) => void;
  /** A file was checked and is now a document of the folder. */
  onAvailable?: (row: UploadRow) => void;
  /** The file was refused after the check (its document or version stays, marked refused). */
  onRejected?: (row: UploadRow) => void;
}

export interface UploadControllerOptions {
  fetcher: ApiFetcher;
  /** Random ids for rows and idempotency keys; `crypto.randomUUID` by default. */
  randomId?: () => string;
}

type Listener = () => void;

const QUIET_LOGGER = {
  debug: () => undefined,
  warn: () => undefined,
  error: () => undefined,
};

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const range = (count: number): number[] => Array.from({ length: count }, (_, index) => index + 1);

/** A request worth repeating: the network dropped, the server is busy, or the first try still runs. */
function isTransient(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  return (
    error.status >= 500 ||
    error.status === 429 ||
    (error.status === 409 && error.code === 'IDEMPOTENCY_KEY_REUSED')
  );
}

export class UploadController {
  private readonly fetcher: ApiFetcher;
  private readonly randomId: () => string;
  private handlers: UploadHandlers = {};
  private readonly listeners = new Set<Listener>();
  private readonly rows = new Map<string, UploadRow>();
  private snapshot: readonly UploadRow[] = [];

  private uppy: Uppy<UploadMeta, UploadBody> | null = null;
  private destroyed = false;
  /** Row id → what Uppy was given for it. */
  private readonly uppyIds = new Map<string, string>();
  private readonly targets = new Map<string, UploadTarget>();
  /** Row id → the metadata or the revision reason sent with the file. */
  private readonly extras = new Map<string, UploadExtras>();
  /** Uppy file id → its upload on the server. */
  private readonly sessions = new Map<string, Session>();

  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private polling = false;
  private readonly scanStartedAt = new Map<string, number>();
  private readonly lastPolledAt = new Map<string, number>();

  constructor(options: UploadControllerOptions) {
    this.fetcher = options.fetcher;
    this.randomId = options.randomId ?? (() => crypto.randomUUID());
  }

  // --- External store.

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): readonly UploadRow[] => this.snapshot;

  setHandlers(handlers: UploadHandlers): void {
    this.handlers = handlers;
  }

  /** Ready (again) after `destroy`: React strict mode runs an effect, its cleanup, then it again. */
  attach(): void {
    this.destroyed = false;
  }

  /** Stops everything: sending files are cancelled (their uploads are aborted on the server). */
  destroy(): void {
    this.destroyed = true;
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.pollTimer = null;
    const uppy = this.uppy;
    this.uppy = null;
    this.rows.clear();
    this.uppyIds.clear();
    this.targets.clear();
    this.extras.clear();
    // `sessions` is kept: aborting the files still being sent (`uppy.destroy()`) reads them.
    this.scanStartedAt.clear();
    this.lastPolledAt.clear();
    this.publish();
    uppy?.destroy();
  }

  /** Read through a method: `destroyed` changes while a request is in flight. */
  private isDestroyed(): boolean {
    return this.destroyed;
  }

  private publish(): void {
    this.snapshot = [...this.rows.values()];
    for (const listener of this.listeners) listener();
  }

  private patch(id: string, change: Partial<UploadRow>): void {
    const row = this.rows.get(id);
    if (!row || this.destroyed) return;
    this.rows.set(id, { ...row, ...change });
    this.publish();
  }

  private settle(id: string, outcome: Classified): void {
    this.scanStartedAt.delete(id);
    this.patch(id, {
      phase: outcome.phase,
      issue: outcome.issue,
      retryable: outcome.retryable,
      slow: false,
    });
  }

  // --- Actions.

  /**
   * Adds files to a folder: each is checked at once, then sent. `extras[i]` goes with `files[i]`
   * (its metadata, or the reason of a new version). Returns the ids of the rows.
   */
  addFiles(
    files: readonly File[],
    target: UploadTarget,
    extras: readonly (UploadExtras | undefined)[] = [],
  ): string[] {
    if (this.destroyed) return [];
    const ids: string[] = [];
    for (const [index, file] of files.entries()) {
      const id = this.randomId();
      ids.push(id);
      const issue = precheck(file);
      this.rows.set(id, {
        id,
        name: file.name,
        size: file.size,
        mimeType: file.type,
        spaceId: target.spaceId,
        folderId: target.folderId,
        phase: issue ? 'refused' : 'sending',
        percent: 0,
        issue,
        slow: false,
        retryable: false,
        documentId: null,
        versionId: null,
      });
      if (issue) continue;
      this.targets.set(id, target);
      const own = extras[index];
      if (own) this.extras.set(id, own);
      try {
        this.uppyIds.set(
          id,
          this.engine().addFile({
            name: file.name,
            type: file.type,
            data: file,
            source: 'batiflow',
            meta: { rowId: id, relativePath: id },
          }),
        );
      } catch {
        this.settle(id, { ...classifyError(null), retryable: false });
      }
    }
    this.publish();
    return ids;
  }

  /** A new attempt for a failed file (a new upload: the failed one was aborted). */
  retry(id: string): void {
    const row = this.rows.get(id);
    const uppyId = this.uppyIds.get(id);
    const uppy = this.uppy;
    if (row?.phase !== 'failed' || !row.retryable || !uppyId || !uppy) return;
    this.patch(id, { phase: 'sending', percent: 0, issue: null });
    uppy.retryUpload(uppyId).catch(() => {
      // Reported through the `upload-error` event.
    });
  }

  /** Stops a file being sent (its upload is aborted) or removes a finished row from the list. */
  remove(id: string): void {
    const row = this.rows.get(id);
    if (!row) return;
    const uppyId = this.uppyIds.get(id);
    this.rows.delete(id);
    this.uppyIds.delete(id);
    this.targets.delete(id);
    this.extras.delete(id);
    this.scanStartedAt.delete(id);
    this.lastPolledAt.delete(id);
    this.publish();
    if (uppyId && this.uppy?.getFile(uppyId)) this.uppy.removeFile(uppyId);
  }

  // --- Uppy.

  private engine(): Uppy<UploadMeta, UploadBody> {
    if (this.uppy) return this.uppy;
    const uppy = new Uppy<UploadMeta, UploadBody>({
      id: 'batiflow-upload',
      // Uppy's default logger prints errors to the console; the screen already explains them.
      logger: QUIET_LOGGER,
      autoProceed: true,
      allowMultipleUploadBatches: true,
    });
    uppy.use(AwsS3<UploadMeta, UploadBody>, {
      shouldUseMultipart: true,
      limit: 4,
      retryDelays: [...uploadTiming.retryDelays],
      getChunkSize: (file) => partSizeFor(file.size),
      createMultipartUpload: (file) => this.createUpload(file),
      signPart: (file, options) => this.signPart(file, options.partNumber),
      listParts: (file) => this.listParts(file),
      completeMultipartUpload: (file, options) =>
        this.complete(file, options.parts, options.signal),
      abortMultipartUpload: (file) => this.abortUpload(file),
    });

    uppy.on('upload-progress', (file, progress) => {
      const id = file?.meta.rowId;
      if (!id || typeof progress.bytesUploaded !== 'number' || !progress.bytesTotal) return;
      const percent = Math.min(
        100,
        Math.floor((progress.bytesUploaded / progress.bytesTotal) * 100),
      );
      if (this.rows.get(id)?.percent !== percent) this.patch(id, { percent });
    });
    uppy.on('s3-multipart:part-uploaded', (file, part) => {
      this.sessions.get(file.id)?.sent.set(part.PartNumber, part.ETag);
    });
    uppy.on('upload-success', (file) => {
      if (file) this.afterSent(file);
    });
    uppy.on('upload-error', (file, error) => {
      const id = file?.meta.rowId;
      if (id && this.rows.has(id)) this.settle(id, classifyError(error));
    });
    this.uppy = uppy;
    return uppy;
  }

  private rowIdOf(file: UploadFile): string {
    return file.meta.rowId;
  }

  private sessionOf(file: UploadFile): Session {
    const session = this.sessions.get(file.id);
    if (!session) throw new Error('upload session missing');
    return session;
  }

  /** POST …/uploads: the server checks type, size, quota and rights, and returns the part URLs. */
  private async createUpload(file: UploadFile): Promise<{ uploadId: string; key: string }> {
    const id = this.rowIdOf(file);
    const row = this.rows.get(id);
    const target = this.targets.get(id);
    const extras = this.extras.get(id);
    if (!row || !target) throw new Error('upload row missing');
    const { data } = await this.fetcher<UploadSessionDtoOutput>({
      method: 'POST',
      url: `/api/v1/spaces/${target.spaceId}/uploads`,
      data: {
        folderId: target.folderId,
        ...(target.documentId ? { documentId: target.documentId } : {}),
        filename: row.name,
        size: row.size,
        mimeType: row.mimeType,
        ...(extras?.metadata ? { metadata: extras.metadata } : {}),
        ...(extras?.revisionReason ? { revisionReason: extras.revisionReason } : {}),
      },
    });
    const partSize = partSizeFor(row.size);
    const session: Session = {
      spaceId: target.spaceId,
      uploadId: data.uploadId,
      partCount: Math.max(1, Math.ceil(row.size / partSize)),
      urls: new Map(data.parts.map((part) => [part.partNumber, part.url])),
      urlsExpireAtMs: Date.parse(data.urlsExpireAt),
      signed: new Set(),
      sent: new Map(),
      refreshing: null,
      idempotencyKey: `up-${this.randomId()}`,
      completed: null,
    };
    if (data.partSize !== partSize || data.parts.length !== session.partCount) {
      // The server cuts files differently from Uppy: sending would fail at completion.
      await this.abortSession(session);
      throw new Error('the server and the browser disagree on the parts');
    }
    this.sessions.set(file.id, session);
    return { uploadId: data.uploadId, key: data.uploadId };
  }

  /**
   * The URL of a part: the one returned at creation, or a fresh one when it is about to expire or
   * when this is a retry (the failure may be an expired URL). One call refreshes every part not
   * sent yet, so a file of many parts does not ask once per part.
   */
  private async signPart(file: UploadFile, partNumber: number) {
    const session = this.sessionOf(file);
    const url = session.urls.get(partNumber);
    const expiring = Date.now() >= session.urlsExpireAtMs - uploadTiming.urlMarginMs;
    if (!url || expiring || session.signed.has(partNumber))
      await this.refreshUrls(session, partNumber);
    session.signed.add(partNumber);
    const fresh = session.urls.get(partNumber);
    if (!fresh) throw new Error('part URL missing');
    return { method: 'PUT' as const, url: fresh, fields: {} };
  }

  private refreshUrls(session: Session, wanted: number): Promise<void> {
    session.refreshing ??= (async () => {
      try {
        const missing = range(session.partCount).filter((n) => !session.sent.has(n));
        const partNumbers = (missing.includes(wanted) ? missing : [...missing, wanted]).slice(
          0,
          1000,
        );
        const { data } = await this.fetcher<UploadPartsDtoOutput>({
          method: 'POST',
          url: `/api/v1/spaces/${session.spaceId}/uploads/${session.uploadId}/parts`,
          data: { partNumbers },
        });
        for (const part of data.parts) session.urls.set(part.partNumber, part.url);
        session.urlsExpireAtMs = Date.parse(data.urlsExpireAt);
      } finally {
        session.refreshing = null;
      }
    })();
    return session.refreshing;
  }

  /** The parts already sent, from what this page saw (the server is not asked). */
  private listParts(file: UploadFile) {
    const session = this.sessionOf(file);
    return [...session.sent].map(([PartNumber, ETag]) => ({ PartNumber, ETag }));
  }

  /**
   * POST …/complete with this upload's Idempotency-Key. Uppy does not repeat this call, and a failed
   * completion would abort an upload whose parts all arrived: it is repeated here (same key, so a
   * request that did reach the server is replayed, not run twice).
   */
  private async complete(
    file: UploadFile,
    parts: readonly { PartNumber?: number; ETag?: string }[],
    signal: AbortSignal,
  ): Promise<Record<string, never>> {
    const session = this.sessionOf(file);
    const body = {
      parts: parts
        .flatMap((part) =>
          part.PartNumber !== undefined && part.ETag !== undefined
            ? [{ partNumber: part.PartNumber, etag: part.ETag }]
            : [],
        )
        .sort((a, b) => a.partNumber - b.partNumber),
    };
    for (let attempt = 0; ; attempt++) {
      try {
        const { data } = await this.fetcher<CompletedUploadDtoOutput>({
          method: 'POST',
          url: `/api/v1/spaces/${session.spaceId}/uploads/${session.uploadId}/complete`,
          data: body,
          idempotencyKey: session.idempotencyKey,
        });
        session.completed = data;
        return {};
      } catch (error) {
        const wait = uploadTiming.retryDelays[attempt];
        if (wait === undefined || !isTransient(error) || signal.aborted) throw error;
        await sleep(wait);
      }
    }
  }

  private async abortUpload(file: UploadFile): Promise<void> {
    const session = this.sessions.get(file.id);
    if (session) await this.abortSession(session);
  }

  /** DELETE …/uploads/{id}: best effort (the upload may be gone already). */
  private async abortSession(session: Session): Promise<void> {
    try {
      await this.fetcher<unknown>({
        method: 'DELETE',
        url: `/api/v1/spaces/${session.spaceId}/uploads/${session.uploadId}`,
      });
    } catch {
      // An upload that cannot be aborted expires by itself (24 h).
    }
  }

  // --- After the upload: the antivirus check.

  private afterSent(file: UploadFile): void {
    const id = this.rowIdOf(file);
    const completed = this.sessions.get(file.id)?.completed;
    if (!this.rows.has(id)) return;
    if (!completed) {
      this.settle(id, { ...classifyError(null), retryable: false });
      return;
    }
    const known = { documentId: completed.documentId, versionId: completed.versionId };
    if (completed.status === 'available') {
      this.markAvailable(id, known);
      return;
    }
    this.patch(id, { phase: 'scanning', percent: 100, issue: null, ...known });
    this.scanStartedAt.set(id, Date.now());
    this.schedulePoll(uploadTiming.pollIntervalMs);
    const row = this.rows.get(id);
    if (row) this.handlers.onCompleted?.(row);
  }

  private markAvailable(id: string, known: { documentId: string; versionId: string }): void {
    this.scanStartedAt.delete(id);
    this.patch(id, { phase: 'added', percent: 100, issue: null, slow: false, ...known });
    const row = this.rows.get(id);
    if (row) this.handlers.onAvailable?.(row);
  }

  /**
   * One timer reads the status of the files being checked, one at a time (the least recently read
   * first), so a batch of files costs one request per interval, not one per file: the upload routes
   * are rate limited.
   */
  private schedulePoll(delay: number): void {
    if (this.pollTimer || this.polling || this.destroyed) return;
    this.pollTimer = setTimeout(() => {
      this.pollTimer = null;
      void this.pollOne();
    }, delay);
  }

  private nextToPoll(): UploadRow | null {
    let next: UploadRow | null = null;
    for (const row of this.rows.values()) {
      if (row.phase !== 'scanning') continue;
      if (!next || (this.lastPolledAt.get(row.id) ?? 0) < (this.lastPolledAt.get(next.id) ?? 0))
        next = row;
    }
    return next;
  }

  private async pollOne(): Promise<void> {
    const row = this.nextToPoll();
    if (!row || this.destroyed) return;
    this.polling = true;
    let delay: number = uploadTiming.pollIntervalMs;
    this.lastPolledAt.set(row.id, Date.now());
    const age = Date.now() - (this.scanStartedAt.get(row.id) ?? Date.now());
    try {
      const uploadId = this.sessions.get(this.uppyIds.get(row.id) ?? '')?.uploadId;
      if (!uploadId) throw new Error('upload session missing');
      const { data } = await this.fetcher<UploadStatusDtoOutput>({
        method: 'GET',
        url: `/api/v1/spaces/${row.spaceId}/uploads/${uploadId}`,
      });
      if (!this.rows.has(row.id)) return;
      if (data.versionStatus === 'available' && data.documentId && data.versionId) {
        this.markAvailable(row.id, { documentId: data.documentId, versionId: data.versionId });
      } else if (data.versionStatus === 'rejected') {
        this.settle(row.id, classifyRejection(data.rejectionReason));
        const rejected = this.rows.get(row.id);
        if (rejected) this.handlers.onRejected?.(rejected);
      } else if (data.status === 'aborted') {
        this.settle(row.id, classifyError(new ApiError(409, 'UPLOAD_EXPIRED')));
      } else if (age > uploadTiming.giveUpAfterMs) {
        this.settle(row.id, statusLost());
      } else if (age > uploadTiming.slowAfterMs && !row.slow) {
        this.patch(row.id, { slow: true });
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 429)
        delay = uploadTiming.slowPollIntervalMs;
      else if (error instanceof ApiError && error.status >= 400 && error.status < 500)
        this.settle(row.id, statusLost());
      // Anything else (network, server error) is tried again at the next turn.
    } finally {
      this.polling = false;
    }
    if (this.isDestroyed()) return;
    const oldest = [...this.scanStartedAt.values()].reduce((a, b) => Math.min(a, b), Date.now());
    if (Date.now() - oldest > uploadTiming.slowAfterMs) delay = uploadTiming.slowPollIntervalMs;
    if (this.nextToPoll()) this.schedulePoll(delay);
  }
}
